/**
 * Better Auth React client.
 *
 * Single-origin in production — calls hit `${BASE_URL}/v1/auth/*` and Caddy on
 * the `web` Railway service reverse-proxies to the internal `api`. In dev,
 * empty BASE_URL + MSW handles the auth endpoints unless `VITE_E2E_REAL_API=1`
 * is set, in which case calls go to the deployed API.
 *
 * Exposed surface:
 *   authClient.signIn.email({ email, password })
 *   authClient.signUp.email({ email, password, name })
 *   authClient.signIn.social({ provider: 'google' })
 *   authClient.signIn.anonymous()
 *   authClient.signOut()
 *   authClient.useSession()   // React hook → { data, isPending, error }
 */

import { anonymousClient } from 'better-auth/client/plugins';
import { createAuthClient } from 'better-auth/react';

function resolveBaseURL(): string {
  const envBase = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
  if (envBase) return `${envBase}/v1/auth`;
  // Browser: use the current origin (Caddy proxies /v1/* to the api service).
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/v1/auth`;
  }
  // jsdom/Vitest fallback — the origin is irrelevant; MSW intercepts.
  return 'http://localhost/v1/auth';
}

export const authClient = createAuthClient({
  baseURL: resolveBaseURL(),
  plugins: [anonymousClient()],
});

export type AuthSession = ReturnType<typeof authClient.useSession>;

export const { useSession, signIn, signUp, signOut, updateUser } = authClient;
